import { useDataProvider, useGetList, useNotify } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import type { GulaInstaller } from "./gulaTypes";
import { formatEmails, parseEmails } from "./installerEmails";
import { Field } from "./SimpleSelect";

const InstallerForm = ({
  installer,
  onSaved,
}: {
  installer: GulaInstaller;
  onSaved: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [email, setEmail] = useState(installer.email ?? "");
  const [phone, setPhone] = useState(installer.phone ?? "");
  const [prefixes, setPrefixes] = useState(
    installer.postal_prefixes.join(", "),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEmail(installer.email ?? "");
    setPhone(installer.phone ?? "");
    setPrefixes(installer.postal_prefixes.join(", "));
    // Reset only when another installer is shown, never while typing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installer.id]);

  const handleSave = async () => {
    const parsed = parseEmails(email);
    if (parsed.invalid.length) {
      notify(`Revisa el email: «${parsed.invalid.join(", ")}» no es válido`, {
        type: "error",
      });
      return;
    }
    const emails = formatEmails(parsed.emails);
    setEmail(emails ?? "");
    setSaving(true);
    try {
      await dataProvider.update("gula_installers", {
        id: installer.id,
        data: {
          email: emails,
          phone: phone.trim() || null,
          postal_prefixes: prefixes
            .split(/[,\s]+/)
            .map((prefix) => prefix.trim())
            .filter((prefix) => /^\d{2,3}$/.test(prefix)),
        },
        previousData: installer,
      });
      notify("Instalador guardado", { type: "success" });
      onSaved();
    } catch (error) {
      console.error(error);
      notify("No se ha podido guardar", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="grid gap-3 sm:grid-cols-4">
        <div className="sm:col-span-4">
          <h3 className="font-semibold">{installer.name}</h3>
          <p className="text-sm text-muted-foreground">
            {installer.zone_label}
            {installer.is_default
              ? " · recibe todo lo que no es de otra zona"
              : ""}
          </p>
        </div>
        <Field label="Email para avisos (varios: sepáralos con comas)">
          <Input
            inputMode="email"
            placeholder="avisos@instalador.es, otro@instalador.es"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onBlur={handleSave}
          />
        </Field>
        <Field label="Teléfono">
          <Input
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            onBlur={handleSave}
          />
        </Field>
        <Field label="Zona (inicio del CP: 2 o 3 cifras)">
          <Input
            value={prefixes}
            disabled={installer.is_default}
            placeholder={installer.is_default ? "Resto" : "08, 17, 25, 43"}
            onChange={(event) => setPrefixes(event.target.value)}
          />
        </Field>
        <div className="flex items-end">
          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/** Who installs in each zone, and where their notification emails go. */
export const InstallersPage = () => {
  const { data = [], refetch } = useGetList<GulaInstaller>("gula_installers", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "id", order: "ASC" },
  });
  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 py-4">
      <div>
        <h1 className="text-2xl font-semibold">Instaladores</h1>
        <p className="text-sm text-muted-foreground">
          Cada cliente se asigna solo según su código postal (gana el prefijo
          más largo). Canarias, Ceuta y Melilla salen como «Sin cobertura». Los
          cambios se guardan al salir de cada campo. En cada pedido puedes
          cambiar el instalador, por ejemplo a Kinetico como apoyo.
        </p>
      </div>
      {data.map((installer) => (
        <InstallerForm
          key={installer.id}
          installer={installer}
          onSaved={refetch}
        />
      ))}
    </div>
  );
};

InstallersPage.path = "/instaladores";
